import React, { useContext, useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  CloseButton,
  Modal,
  Alert
} from "react-bootstrap";
import { CartContext } from "../context/CartContext";
import { Link, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaTrash } from "react-icons/fa";
import { useTranslation } from "react-i18next";

const Cart = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cart, updateCartItemQuantity, removeFromCart, clearCart } =
    useContext(CartContext);

  const DELIVERY_PRICE = 1.25;

  // Calculate item price - FIXED: Respect bottle variants and capacity
  const calculateItemPrice = (product) => {
    if (!product) return 0;

    // If product has selected capacity, use its price directly (NO discount)
    if (product.selectedCapacity) {
      return product.price;
    }

    // If product has selected bottle variant, use its final price
    if (product.selectedBottleVariant) {
      return product.selectedBottleVariant.finalPrice || product.price;
    }

    // Otherwise apply main product discount if available
    return product.discount > 0
      ? product.price * (1 - product.discount / 100)
      : product.price;
  };

  const getProductImage = (product) => {
    if (!product) return "";

    if (product.image) {
      return product.image;
    }
    if (product.images && product.images.length > 0) {
      return product.images[0];
    } else if (product.images && typeof product.images === 'string') {
      return product.images;
    }

    return "";
  };

  // Calculate subtotal
  const calculateSubtotal = () => {
    return cart.reduce(
      (total, cartItem) =>
        total + calculateItemPrice(cartItem.product) * cartItem.quantity,
      0
    );
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    const timer = setTimeout(() => {
      const formContainer = document.querySelector('.delivery-form') ||
        document.querySelector('form') ||
        document.querySelector('.mt-top-cartpage') ||
        document.querySelector('.cart-items-sidebar');

      if (formContainer) {
        formContainer.scrollTop = 0;

        if (window.location.pathname.includes('checkout')) {
          const firstInput = document.querySelector('input, select, textarea');
          if (firstInput) {
            setTimeout(() => firstInput.focus(), 100);
          }
        }
      }
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  // Calculate total discount - FIXED: Skip if capacity selected
  const calculateTotalDiscount = () => {
    return cart.reduce((total, cartItem) => {
      const product = cartItem.product;
      if (!product) return total;

      // If product has selected capacity, NO discount applies
      if (product.selectedCapacity) {
        return total;
      }

      if (product.discount > 0) {
        const originalPrice = product.price * cartItem.quantity;
        const discountedPrice = calculateItemPrice(product) * cartItem.quantity;
        return total + (originalPrice - discountedPrice);
      }
      return total;
    }, 0);
  };

  const subtotal = calculateSubtotal();
  const isFreeDelivery = subtotal >= 40;
  const deliveryFee = isFreeDelivery ? 0 : DELIVERY_PRICE;
  const total = subtotal + deliveryFee;
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const totalItems = cart.reduce((count, cartItem) => count + cartItem.quantity, 0);
  const [showStockAlert, setShowStockAlert] = useState(false);
  const [alertProduct, setAlertProduct] = useState(null);

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setShowImageModal(true);
  };

  const handleCloseModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
  };

  // Helper function to get available stock for a cart item
  const getAvailableStock = (cartItem) => {
    const { product } = cartItem;

    if (!product) return 0;

    console.log("Checking stock for:", {
      productId: product._id,
      productTitle: product.title,
      selectedColor: product.selectedColor,
      selectedCapacity: product.selectedCapacity,
      productData: product
    });

    // If product has a selected capacity, use its stock
    if (product.selectedCapacity) {
      return product.selectedCapacity.stock || 0;
    }

    // If product has a selected color
    if (product.selectedColor) {
      const possibleStockSources = [
        product.colorStockQuantity,
        product.colorStocks?.find(stock =>
          stock.color === product.selectedColor?.id ||
          stock.colorId === product.selectedColor?.id ||
          stock.colorName === product.selectedColor?.name
        )?.quantity,
        product.selectedColor?.quantity,
        product.availableColorStocks?.find(c => c.id === product.selectedColor?.id)?.quantity,
      ];

      for (const stock of possibleStockSources) {
        if (stock !== undefined && stock !== null) {
          return stock;
        }
      }

      return product.Item || 0;
    }

    return product.Item || 0;
  };

  const handleIncreaseQuantity = (cartItem, currentQuantity) => {
    if (!cartItem || !cartItem.product) {
      console.error("Invalid cartItem:", cartItem);
      return;
    }

    const availableStock = getAvailableStock(cartItem);
    const product = cartItem.product;

    if (availableStock > 0 && currentQuantity < availableStock) {
      updateCartItemQuantity(
        product._id,
        currentQuantity + 1,
        product.selectedColor?.id,
        product.selectedCapacity?._id // Pass capacity ID
      );
      setShowStockAlert(false);
    } else {
      let stockMessage = "";

      if (product.selectedCapacity) {
        stockMessage = `${t("Only")} ${availableStock} ${t("items available for")} ${product.selectedCapacity.name}`;
      } else if (product.selectedColor) {
        stockMessage = `${t("Only")} ${availableStock} ${t("items available for")} ${product.selectedColor.name} ${t("color")}`;
      } else {
        stockMessage = `${t("Only")} ${availableStock} ${t("items available in stock")}`;
      }

      setAlertProduct({
        ...product,
        stockMessage
      });
      setShowStockAlert(true);

      setTimeout(() => {
        setShowStockAlert(false);
      }, 5000);
    }
  };

  // Render empty state
  if (cart.length === 0) {
    return (
      <Container className="text-center cart-mt" style={{ fontFamily: "'Hanuman', Arial, sans-serif" }}>
        <h3>{t('Your Cart is empty.')}</h3>
        <Link to="/men-perfume" className="btn btn-primary btn-color btn-card-hover border-0 px-4 py-2 mt-5">
          {t('Continue Shopping')}
        </Link>
      </Container>
    );
  }

  return (
    <Container fluid className="my-5 px-4 mt-top-cartpage" style={{ fontFamily: "'Hanuman', Arial, sans-serif" }}>
      <Row>
        {/* Left Sidebar - Shopping Cart Products */}
        <Col md={8} className="cart-items-sidebar mt-top-cartpage new">
          {cart.map((cartItem) => {
            const product = cartItem.product;

            if (!product) {
              console.error("Cart item has no product:", cartItem);
              return null;
            }

            const itemPrice = calculateItemPrice(product);
            const totalItemPrice = itemPrice * cartItem.quantity;

            // Get display title with bottle size, capacity, and color info - WITH "bottle" word
            const getDisplayTitle = () => {
              let title = product.title;
              const parts = [];

              // If there's a bottle variant, show the bottle size with "bottle" word
              if (product.selectedBottleVariant) {
                parts.push(
                  <span key="bottle" className="capacity-highlight" data-ml={product.selectedBottleVariant.bottleSize}>
                    {product.selectedBottleVariant.bottleSize}ml bottle
                  </span>
                );
              }

              // If there's a capacity selected, show it too
              if (product.selectedCapacity) {
                const capacityName = product.selectedCapacity.name;
                const isMl = /^\d+ml$/i.test(capacityName) || /^\d+\s*ml$/i.test(capacityName);
                const isFullBox = /full box/i.test(capacityName);

                if (isMl || isFullBox) {
                  const mlMatch = capacityName.match(/(\d+)/);
                  const mlValue = mlMatch ? mlMatch[1] : '';
                  const className = isFullBox ? 'capacity-highlight-fullbox' : 'capacity-highlight';

                  parts.push(
                    <span
                      key="capacity"
                      className={className}
                      data-ml={mlValue}
                    >
                      {capacityName}
                    </span>
                  );
                } else {
                  parts.push(
                    <span key="capacity" className="capacity-highlight">
                      {capacityName}
                    </span>
                  );
                }
              }

              // Add color if selected
              if (product.selectedColor) {
                parts.push(
                  <span key="color" style={{ color: product.selectedColor.hexCode || '#333' }}>
                    {product.selectedColor.name}
                  </span>
                );
              }

              if (parts.length > 0) {
                return (
                  <span style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
                    <span className="product-title-text">{title}</span>
                    <span className="title-variant-spacer"> </span>
                    {parts.map((part, index) => (
                      <React.Fragment key={index}>
                        {index > 0 && <span className="variant-separator"> - </span>}
                        {part}
                      </React.Fragment>
                    ))}
                  </span>
                );
              }

              return title;
            };

            return (
              <Card key={`${product._id}-${product.selectedBottleVariant?.bottleSize || ''}-${product.selectedCapacity?._id || ''}-${product.selectedColor?.id || ''}`} className="mb-3">                <Card.Body className="product-cart d-flex align-items-center">
                <div className="me-3 position-relative cart-size">
                  {getProductImage(product) ? (
                    <img
                      src={getProductImage(product)}
                      alt={product.title}
                      className="rounded-2 card-img-style cursor-pointer"
                      style={{
                        width: "100px",
                        height: "100px",
                        objectFit: "cover",
                        transition: "transform 0.2s ease",
                      }}
                      onClick={() => handleImageClick(getProductImage(product))}
                      onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
                      onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                      onError={(e) => {
                        e.target.src = "/images/placeholder-image.jpg";
                      }}
                    />
                  ) : (
                    <div
                      className="rounded-2 d-flex align-items-center justify-content-center bg-light"
                      style={{
                        width: "100px",
                        height: "100px",
                        border: "1px dashed #ccc"
                      }}
                    >
                      <span className="text-muted small">No Image</span>
                    </div>
                  )}
                  {product.discount > 0 && (
                    <span
                      className="position-absolute top-0 start-0 bg-danger text-white px-2 py-1 rounded discount-screenphone"
                      style={{ fontSize: "0.7rem" }}
                    >
                      -{product.discount}%
                    </span>
                  )}
                </div>
                <div className="flex-grow-1">
                  <h6 className="product-title-phonescreen">
                    {getDisplayTitle()}
                  </h6>
                  <div className="d-flex align-items-center">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() =>
                        updateCartItemQuantity(
                          product._id,
                          cartItem.quantity - 1,
                          product.selectedColor?.id,
                          product.selectedCapacity?._id,
                          product.selectedBottleVariant?.bottleSize
                        )
                      }
                      disabled={cartItem.quantity <= 1}
                      className="me-md-2 btn-decrease-size"
                    >
                      <span className="item-count">-</span>
                    </Button>

                    <span className="mx-2 item-count">{cartItem.quantity}</span>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => handleIncreaseQuantity(cartItem, cartItem.quantity)}
                      className="me-2 btn-decrease-size"
                    >
                      <span className="item-count">+</span>
                    </Button>

                    {/* Price and close button - FIXED */}
                    <div className="ms-auto text-end">
                      {product.selectedCapacity ? (
                        // For capacity selections: just show the price (no discount)
                        <span className="screen-phone-fs fw-bold">
                          ${totalItemPrice.toFixed(2)}
                        </span>
                      ) : product.selectedBottleVariant && product.selectedBottleVariant.hasDiscount ? (
                        // For bottle variants with discount
                        <div>
                          <span className="screen-phone-fs text-muted text-decoration-line-through me-2">
                            ${(product.selectedBottleVariant.sellingPrice * cartItem.quantity).toFixed(2)}
                          </span>
                          <span className="screen-phone-fs text-danger fw-bold">
                            ${totalItemPrice.toFixed(2)}
                          </span>
                          <span className="ms-1 badge bg-success">
                            -{product.selectedBottleVariant.discountType === 'percentage'
                              ? `${product.selectedBottleVariant.discountValue}%`
                              : `$${product.selectedBottleVariant.discountValue}`}
                          </span>
                        </div>
                      ) : product.discount > 0 ? (
                        // For products with discount (no variant selected)
                        <div>
                          <span className="screen-phone-fs text-muted text-decoration-line-through me-2">
                            ${(product.price * cartItem.quantity).toFixed(2)}
                          </span>
                          <span className="screen-phone-fs text-danger fw-bold">
                            ${totalItemPrice.toFixed(2)}
                          </span>
                        </div>
                      ) : (
                        // For regular products
                        <span className="screen-phone-fs fw-bold">
                          ${totalItemPrice.toFixed(2)}
                        </span>
                      )}
                    </div>
                    <CloseButton
                      className="ms-3 close-phonesize"
                      variant="danger"
                      onClick={() => removeFromCart(
                        product._id,
                        product.selectedColor?.id,
                        product.selectedCapacity?._id
                      )}
                    />
                  </div>
                  {/* Stock Alert Message */}
                  {showStockAlert && alertProduct && alertProduct._id === product._id && (
                    <Alert variant="warning" className="mt-2 mb-0 py-2">
                      {alertProduct.stockMessage || `${t("Only")} ${alertProduct.Item} ${t("items available in stock")}`}
                    </Alert>
                  )}
                </div>
              </Card.Body>
              </Card>
            );
          })}
        </Col>

        {/* Right Sidebar - Order Summary */}
        <Col md={4} className="order-summary-sidebar mt-top-cartpage new-1">
          <Card>
            <Card.Header className="py-3 summary-header">
              <h5 className="mb-0 text-uppercase"> {t('Order Summary')} </h5>
            </Card.Header>
            <Card.Body>
              {/* Total Items */}
              <div className="d-flex justify-content-between my-4">
                <span>{t('Total Item')}</span>
                <span>{totalItems}</span>
              </div>

              {/* Main Total (Before Discount) - FIXED */}
              <div className="d-flex my-2 justify-content-between my-4">
                <span>{t('Main Total')}</span>
                <span>
                  ${cart.reduce((total, cartItem) => {
                    const product = cartItem.product;
                    if (!product) return total;

                    // For bottle variants with discount, show the original price
                    if (product.selectedBottleVariant && product.selectedBottleVariant.hasDiscount) {
                      return total + (product.selectedBottleVariant.sellingPrice * cartItem.quantity);
                    }

                    // For capacity selections, use the capacity price
                    if (product.selectedCapacity) {
                      return total + (product.price * cartItem.quantity);
                    }

                    // For regular products, use the product price
                    return total + (product.price * cartItem.quantity);
                  }, 0).toFixed(2)}
                </span>
              </div>

              {/* Discount Calculation - FIXED */}
              {(() => {
                const totalDiscount = cart.reduce((total, cartItem) => {
                  const product = cartItem.product;
                  if (!product) return total;

                  // For bottle variants with discount
                  if (product.selectedBottleVariant && product.selectedBottleVariant.hasDiscount) {
                    const originalPrice = product.selectedBottleVariant.sellingPrice;
                    const finalPrice = product.selectedBottleVariant.finalPrice;
                    return total + ((originalPrice - finalPrice) * cartItem.quantity);
                  }

                  // For capacity selections, NO discount
                  if (product.selectedCapacity) {
                    return total;
                  }

                  // For regular products with discount
                  if (product.discount > 0) {
                    const originalPrice = product.price;
                    const discountedPrice = product.price * (1 - product.discount / 100);
                    return total + ((originalPrice - discountedPrice) * cartItem.quantity);
                  }
                  return total;
                }, 0);

                return totalDiscount > 0 ? (
                  <div className="d-flex justify-content-between my-4">
                    <span className="text-danger">{t('Total Discount')}</span>
                    <span className="text-danger">
                      -${totalDiscount.toFixed(2)}
                    </span>
                  </div>
                ) : null;
              })()}

              <div
                style={{
                  borderBottom: "1px dashed #333",
                  width: "100%",
                  margin: "20px 0"
                }}
              ></div>

              {/* Subtotal (Before Discount) */}
              <div className="d-flex justify-content-between">
                <span className="fs-5" style={{ fontWeight: "500" }}>{t('Subtotal')}</span>
                <span className="fs-5" style={{ fontWeight: "bold" }}>${subtotal.toFixed(2)}</span>
              </div>
            </Card.Body>
            <Card.Footer className="text-center p-0 border-0 bg-transparent">
              <Button
                variant=""
                className="w-100 btn-color btn-card-hover text-white fw-medium mt-2"
                onClick={() =>
                  navigate("/checkout", {
                    state: {
                      subtotal,
                      cart: cart.map(cartItem => ({
                        product: {
                          _id: cartItem.product ? cartItem.product._id : null,
                          title: cartItem.product ? cartItem.product.title : "Unknown",
                          displayTitle: cartItem.product?.displayTitle || cartItem.product?.title,
                          price: cartItem.product ? cartItem.product.price : 0,
                          discount: cartItem.product ? cartItem.product.discount : 0,
                          image: getProductImage(cartItem.product),
                          images: cartItem.product?.images || [cartItem.product?.image],
                          Item: cartItem.product ? cartItem.product.Item : 0,
                          selectedColor: cartItem.product?.selectedColor || null,
                          selectedCapacity: cartItem.product?.selectedCapacity || null
                        },
                        quantity: cartItem.quantity
                      }))
                    }
                  })
                }
              >
                {t('Proceed to Checkout')}
              </Button>
            </Card.Footer>
          </Card>
        </Col>
        <Col md={8}
          className="mt-md-4 mt-5 ps-2 p-0 d-flex justify-content-between align-items-center mb-4"
        >
          <div
            className="d-flex align-items-center back-link cursor-pointer"
            onClick={() => navigate("/shop")}
          >
            <FaArrowLeft className="me-2 back-icon text-dark" />
            <h5 className="mb-0 text-dark">{t('BACK TO SHOP')}</h5>
          </div>
          <Button
            variant="outline-danger"
            onClick={clearCart}
            className="d-flex align-items-center"
          >
            <FaTrash className="me-2" /> {t('Clear Cart')}
          </Button>
        </Col>
      </Row>

      <Modal
        show={showImageModal}
        onHide={handleCloseModal}
        centered
        size="md"
        backdropClassName="bg-dark"
        contentClassName="border-0 bg-transparent"
      >
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-pink-500/30 via-rose-300/20 to-yellow-300/30 backdrop-blur-md"
          onClick={handleCloseModal}
        >
          <div
            className="relative max-w-3xl mx-auto bg-white rounded-3 shadow-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <Modal.Body className="p-0">
              <img
                src={selectedImage}
                alt="Full Preview"
                className="w-100 h-auto object-contain rounded-bottom-3"
              />
            </Modal.Body>
          </div>
        </div>
      </Modal>
    </Container>
  );
};

export default Cart;

// Correct with 579 line code changes
